/**
 * Between a saved KPI and the modal's fields: the target and alert forms
 * hold what a person types (rands, percentages, text); the draft holds the
 * stored units the API wants.
 */

import type { MetricFormat } from '../reports/reports-api.ts';
import { toStored, toTyped } from './kpi-format.ts';
import type { AlertForm, TargetForm } from './KpiGoalPanels.tsx';
import type { Kpi, KpiAlert, KpiTarget } from './kpis-api.ts';

export function todayIso(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function targetFormOf(kpi: Kpi | null, format: MetricFormat): TargetForm {
  const condition = kpi?.target_condition ?? '';
  return {
    enabled: Boolean(kpi?.target_type) || kpi === null,
    type: kpi?.target_type ?? 'RECURRING',
    period: kpi?.target_period ?? 'MONTHLY',
    condition,
    value:
      kpi?.target_value !== null && kpi?.target_value !== undefined
        ? String(toTyped(kpi.target_value, format, condition))
        : '',
    start: kpi?.target_start ?? todayIso(),
    due: kpi?.target_due ?? '',
    description: kpi?.target_description ?? '',
  };
}

export function alertFormOf(kpi: Kpi | null, format: MetricFormat): AlertForm {
  const condition = kpi?.alert_condition ?? '';
  return {
    enabled: Boolean(kpi?.alert_condition),
    condition,
    value:
      kpi?.alert_value !== null && kpi?.alert_value !== undefined
        ? String(toTyped(kpi.alert_value, format, condition))
        : '',
    interval: kpi?.alert_interval ?? 'MONTH',
    description: kpi?.alert_description ?? '',
  };
}

function typedNumber(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** The target to send, or a sentence saying what is missing. */
export function targetOf(form: TargetForm, format: MetricFormat): KpiTarget | null | string {
  if (!form.enabled) return null;
  const value = typedNumber(form.value);
  if (!form.condition) return 'Choose a condition for the target.';
  if (value === null) return 'Enter a target value.';
  if (form.type === 'ONE_TIME' && !form.start) return 'Choose a start date for the target.';
  return {
    type: form.type,
    period: form.type === 'RECURRING' ? form.period : null,
    condition: form.condition,
    value: toStored(value, format, form.condition),
    start: form.type === 'ONE_TIME' ? form.start : null,
    due: form.type === 'ONE_TIME' && form.due ? form.due : null,
    description: form.description.trim() || null,
  };
}

export function alertOf(form: AlertForm, format: MetricFormat): KpiAlert | null | string {
  if (!form.enabled) return null;
  const value = typedNumber(form.value);
  if (!form.condition) return 'Choose a condition for the alert.';
  if (value === null) return 'Enter a value for the alert.';
  return {
    condition: form.condition,
    value: toStored(value, format, form.condition),
    interval: form.interval,
    description: form.description.trim() || null,
  };
}
